import { START_LOADING, COMPLETE_LOADING } from '@/store';
import i18n from '@/plugins/i18n';
import { DEFAULT_LOCALE_EN_US } from '@/constants/i18n';
import { mapToI18nObject, normalizeLocaleId, sanitizeLanguage, updateD3Locale } from '@/utils/i18n';
import { setMomentLocale } from '@/utils/date';

import * as languageService from '@/services/language';
import { getBaseUrl } from '@/utils/interaction';
import isNewTranslation, { state } from './state';
import { CLEAR_AVAILABLE_TRANSLATION, SET_AVAILABLE_TRANSLATION, SET_FORMAT_LOCALE, SET_LOCALE } from './constants';

const actions = {
  /**
   * payload.locale: the interface language (locale id, e.g. EN_US)
   * payload.format: the regional format (locale id, e.g. EN_GB), without one the format of the language's locale is used
   */
  setLocale({ commit, rootGetters }, payload) {
    const localeObj = rootGetters.systemLocales;

    let localeObject;
    let formatLocaleObject;
    if (localeObj) {
      // only allowed locales
      localeObject = localeObj[payload?.locale] || DEFAULT_LOCALE_EN_US;
      formatLocaleObject = localeObj[normalizeLocaleId(payload?.format)] || localeObject;
    } else {
      localeObject = DEFAULT_LOCALE_EN_US;
      formatLocaleObject = DEFAULT_LOCALE_EN_US;
    }

    commit(SET_LOCALE, localeObject);
    commit(SET_FORMAT_LOCALE, formatLocaleObject);
    updateD3Locale(formatLocaleObject.id);
    setMomentLocale(localeObject.language);
  },

  async fetchTranslations({ commit }, payload) {
    commit(START_LOADING);
    const { textType, forcedLanguage, filter } = payload;
    let { language } = state.locale;

    if (forcedLanguage !== undefined && forcedLanguage !== null) {
      language = sanitizeLanguage(forcedLanguage);
    }

    const translatedTextTypeKey = `${textType}_${language}`;
    if (isNewTranslation(state, translatedTextTypeKey) === undefined) {
      const textTypeTranslation = {
        key: translatedTextTypeKey,
        textType,
        language,
      };
      commit(SET_AVAILABLE_TRANSLATION, textTypeTranslation);
      try {
        const translations = await languageService.fetchTranslations(language, textType, filter);
        const texts = mapToI18nObject(translations);

        // create translation key which holds the base URL of the application,
        // in order to allow other translation keys to dynamically reference this URL.
        // Referencing translations is done by using linked locale messages,
        // e.g.: @:BASE_URL see below link for more information
        //   https://kazupon.github.io/vue-i18n/guide/messages.html#linked-locale-messages
        texts.BASE_URL = getBaseUrl();
        i18n.mergeLocaleMessage(language, texts);
      } catch (e) {
        console.error(e);
      }
    }
    commit(COMPLETE_LOADING);
  },

  async reloadTranslations({ commit, dispatch }) {
    const translationsToReload = state.availableTranslations;
    commit(CLEAR_AVAILABLE_TRANSLATION);
    return Promise.all(
      translationsToReload.map((value) => {
        return dispatch('fetchTranslations', {
          textType: value.textType,
          forcedLanguage: value.language,
        });
      })
    );
  },

  async clearTranslations({ rootGetters }) {
    const localeLanguages = Object.values(rootGetters.systemLocales).map((locale) => locale.language);

    const emptyLocales = localeLanguages.reduce((acc, key) => {
      acc[key] = {};
      return acc;
    }, {});

    localeLanguages.forEach((localeKey) => {
      i18n.setLocaleMessage(localeKey, emptyLocales);
    });
  },
};

export default actions;
